import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Check, Crown, ExternalLink, LogOut } from 'lucide-react';
import { api } from '../api.js';
import { useAuth } from '../auth.jsx';

export default function Settings() {
  const { user, loading, logout, refresh } = useAuth();
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);
  const [params] = useSearchParams();
  const checkout = params.get('checkout'); // 'success' | 'cancel'

  // After returning from Stripe Checkout, the webhook may lag a moment — refresh.
  useEffect(() => {
    if (checkout === 'success') {
      const id = setInterval(refresh, 2000);
      const stop = setTimeout(() => clearInterval(id), 12000);
      return () => { clearInterval(id); clearTimeout(stop); };
    }
  }, [checkout, refresh]);

  if (loading || !user) {
    return (
      <div className="h-full flex items-center justify-center gap-2" style={{ background: 'var(--bg)', color: 'var(--text-muted)' }}>
        <Loader2 size={18} className="spin" /> Loading…
      </div>
    );
  }

  const isPremium = user.plan === 'premium';

  async function upgrade() {
    setBusy(true); setError(null);
    try {
      const { url } = await api.billing.checkout();
      window.location.href = url;
    } catch (e) { setError(e.message); setBusy(false); }
  }

  async function manageBilling() {
    setBusy(true); setError(null);
    try {
      const { url } = await api.billing.portal();
      window.location.href = url;
    } catch (e) { setError(e.message); setBusy(false); }
  }

  return (
    <div className="h-full overflow-y-auto" style={{ background: 'var(--bg)' }}>
      <div className="max-w-3xl mx-auto px-6 py-10">
        <h1 style={{ fontFamily: 'IBM Plex Mono', fontSize: '1.5rem', fontWeight: 600, color: 'var(--text)', margin: '0 0 24px' }}>
          Settings
        </h1>

        {checkout === 'success' && (
          <Banner ok>Payment received — your Premium plan is being activated.</Banner>
        )}
        {checkout === 'cancel' && (
          <Banner>Checkout canceled. You’re still on the Free plan.</Banner>
        )}
        {error && <Banner error>{error}</Banner>}

        {/* Account */}
        <Section title="Account">
          <div className="flex items-center gap-3">
            {user.avatar_url
              ? <img src={user.avatar_url} alt="" style={{ width: 44, height: 44, borderRadius: '50%' }} />
              : <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--surface-2)' }} />}
            <div>
              <div style={{ color: 'var(--text)', fontWeight: 600 }}>{user.name || user.email}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: '.82rem' }}>{user.email}</div>
            </div>
            <div className="flex-1" />
            <button
              onClick={logout}
              style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'none', border: '1px solid var(--border-2)', color: 'var(--text-dim)', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: '.85rem' }}
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </Section>

        {/* Plan */}
        <Section title="Plan">
          <div className="flex items-center gap-2 mb-4">
            <span
              className="badge"
              style={{
                background: isPremium ? 'rgba(234,179,8,.15)' : 'var(--surface-2)',
                color: isPremium ? 'var(--amber)' : 'var(--text-dim)',
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999,
                fontWeight: 600,
              }}
            >
              {isPremium && <Crown size={13} />}
              {isPremium ? 'Premium' : 'Free'}
            </span>
            <span style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>
              {isPremium ? 'Unlimited workflows' : `Up to ${user.workflow_limit} workflows`}
            </span>
          </div>

          {isPremium ? (
            <button className="btn-ghost" onClick={manageBilling} disabled={busy}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {busy ? <Loader2 size={15} className="spin" /> : <ExternalLink size={15} />}
              Manage billing
            </button>
          ) : (
            <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 20, background: 'var(--surface)' }}>
              <div style={{ color: 'var(--text)', fontWeight: 600, marginBottom: 10 }}>Upgrade to Premium</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px', color: 'var(--text-dim)', fontSize: '.88rem' }}>
                {['Unlimited workflows', 'Everything in Free', 'Cancel anytime'].map((f) => (
                  <li key={f} className="flex items-center gap-2" style={{ marginBottom: 6 }}>
                    <Check size={15} style={{ color: 'var(--green)' }} /> {f}
                  </li>
                ))}
              </ul>
              <button className="btn-primary" onClick={upgrade} disabled={busy}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {busy ? <Loader2 size={15} className="spin" /> : <Crown size={15} />}
                Upgrade to Premium
              </button>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: '.8rem', textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', margin: '0 0 12px' }}>
        {title}
      </h2>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
        {children}
      </div>
    </div>
  );
}

function Banner({ children, ok, error }) {
  const c = ok ? 'var(--green)' : error ? 'var(--red)' : 'var(--text-dim)';
  const bg = ok ? 'rgba(34,197,94,.1)' : error ? 'rgba(239,68,68,.1)' : 'var(--surface-2)';
  return (
    <div style={{ background: bg, border: `1px solid ${c}33`, color: c, borderRadius: 10, padding: '12px 16px', marginBottom: 18, fontSize: '.86rem' }}>
      {children}
    </div>
  );
}
